import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Input, Button, Alert, Card, Checkbox, Row } from "antd";
import SimpleReactValidator from "simple-react-validator";
import AuthServices from "../../services/AuthServices";

import "simple-react-validator/dist/locale/es";
import { translate, get_language } from "../../locale";
import { decodeToken } from "react-jwt";
import _ from "lodash";
import ReCAPTCHA from "react-google-recaptcha";
import PropTypes from "prop-types";

const messageElement = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const Login = ({ onSuccess }) => {
  const [values, setValues] = useState({
    username: "",
    password: "",
    remember_me: false,
    recaptcha: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const navigate = useNavigate();

  const simpleValidator = useRef(
    new SimpleReactValidator({
      locale: get_language,
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: messageElement,
    })
  );

  const disabled = () => {
    return !simpleValidator.current.allValid() || values.recaptcha === false;
  };

  useEffect(() => {
    const tk = AuthServices.getToken();
    if (tk !== undefined && tk !== null) {
      navigate("/admin");
    }
  }, [navigate]);

  const roleAllowed = (roles) => {
    const allow = [
      "eira-super-admin",
      "eira-company-admin",
      "eira-company-coordinator",
      "eira-professional",
    ];

    return _.intersection(allow, roles).length > 0;
  };

  const processResponse = (data, decodedToken) => {
    if (roleAllowed(decodedToken.resource_access["eira-backend"].roles)) {
      AuthServices.setRemember(values.remember_me);
      AuthServices.setToken(data.access_token);
      AuthServices.setRefreshToken(data.refresh_token);
      AuthServices.setUser(values.username);

      setLoading(false);

      const nextUrl = sessionStorage.getItem("nextUrl");

      if (nextUrl) {
        sessionStorage.removeItem("nextUrl");
        navigate(nextUrl);
      } else {
        navigate("/admin");
      }
      if (onSuccess) {
        onSuccess();
      }
    } else {
      setError("No tienes permisos para ingresar.");
    }
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("El usuario y/o contraseña son incorrectos.");
      return;
    }
    try {
      const data = {
        username: values.username,
        password: values.password,
      };
      const res = await AuthServices.signIn(data);
      if (
        res?.status !== undefined &&
        res.status >= 200 &&
        res.status < 300 &&
        res.data
      ) {
        const decodedToken = decodeToken(res.data.access_token);
        processResponse(res.data, decodedToken);
      } else {
        setError("El usuario y/o contraseña son incorrectos.");
      }
      setLoading(false);
    } catch (e) {
      if (e.message === "User is not confirmed.") {
        navigate("/auth/login");
      } else if (e.message === "Incorrect username or password.") {
        setError("El usuario y/o contraseña son incorrectos.");
      } else {
        setError(e.message);
      }
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };
  const onChangeRemember = (e) => {
    const { checked } = e.target;
    setValues((v) => ({ ...v, remember_me: checked }));
  };
  const handleReCAPTCHAChange = async (token) => {
    setValues((v) => ({ ...v, recaptcha: false }));
    setError("");
    setLoading(true);
    try {
      if (token !== null) {
        const res = await AuthServices.validateCaptcha(token);
        if (
          res?.status !== undefined &&
          res.status >= 200 &&
          res.status < 300 &&
          res.data.success === true
        ) {
          setValues((v) => ({ ...v, recaptcha: true }));
        } else {
          setError("El captcha es incorrecto.");
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex flex-row w-screen justify-center items-stretch min-h-full max-w-7xl py-24">
      <div className="w-full md:w-1/2 flex justify-center">
        <Card
          title={translate("Iniciar sesión")}
          className="w-full lg:w-7/12"
          bordered={false}
          headStyle={{ border: "none" }}
        >
          {error && (
            <Alert
              message={error}
              type="error"
              className="mb-6"
              closable
              onClose={() => setError("")}
            />
          )}
          <Form
            initialValues={{ remember_me: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            className="w-full"
          >
            <Form.Item>
              <Input
                placeholder={translate("Nombre de usuario")}
                className="border-custom_primary"
                name="username"
                data-testid="login-input-email"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {simpleValidator.current.message(
                "username",
                values.username,
                "required",
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Nombre de usuario requerido",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Form.Item>
              <Input.Password
                placeholder={translate("Contraseña")}
                className="border-custom_primary"
                name="password"
                data-testid="login-input-password"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {simpleValidator.current.message(
                "password",
                values.password,
                "required|min:6",
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Contraseña de usuario requerido",
                        min: "El tamaño de Contraseña debe ser de al menos :min caracteres.",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Form.Item>
              <div data-testid="login-recaptcha">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleReCAPTCHAChange}
                />
              </div>
            </Form.Item>
            <Form.Item>
              <Checkbox
                name="remember_me"
                data-testid="login-checkbox-remember"
                onChange={onChangeRemember}
              >
                {translate("Recordar cuenta")}
              </Checkbox>
            </Form.Item>
            <Row justify="end">
              <Button
                disabled={disabled() || loading}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
                loading={loading}
                data-testid="login-button-submit"
              >
                {translate("Iniciar sesión")}
              </Button>
            </Row>
            <Row className="mt-20">
              <Link
                to="/auth/forgot-password"
                className="mb-4"
                data-testid="login-link-forgot-password"
              >
                {translate("¿Olvidó su contraseña?")}
              </Link>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

Login.propTypes = {
  onSuccess: PropTypes.func,
};

export default Login;
